import { ethers } from "ethers";

import {
  readPaused,
  readPricePerTrdxUsdc,
  readSaleInventory,
  readTokensPerEth,
} from "./trdxSaleService";
import { isTrdxSaleEnvExplicitlyConfigured } from "./trdxSaleEnv";

const TRDX_DECIMALS = 18;
const USDC_DECIMALS = 6;

function formatTrdxAmount(humanNumber) {
  if (!Number.isFinite(humanNumber)) return "—";
  if (humanNumber >= 1000) {
    return humanNumber.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }
  return humanNumber.toLocaleString("en-US", { maximumFractionDigits: 4 });
}

/** @param {import("ethers").BigNumber | null | undefined} tokensPerEthWei */
export function formatEthRateLabel(tokensPerEthWei) {
  if (tokensPerEthWei == null) return "Rate loading…";
  try {
    const perEth = Number(ethers.utils.formatUnits(tokensPerEthWei, TRDX_DECIMALS));
    return `1 ETH = ${formatTrdxAmount(perEth)} TRDX`;
  } catch {
    return "Current rate shown at checkout.";
  }
}

/** @param {import("ethers").BigNumber | null | undefined} pricePerTrdxUsdcRaw USDC (6 dec) per 1 TRDX (1e18 wei). */
export function formatUsdcPriceLabel(pricePerTrdxUsdcRaw) {
  if (pricePerTrdxUsdcRaw == null) return "Price loading…";
  try {
    const usdcPerTrdx = Number(pricePerTrdxUsdcRaw) / 10 ** USDC_DECIMALS;
    if (!Number.isFinite(usdcPerTrdx) || usdcPerTrdx <= 0) return "Current rate shown at checkout.";
    return `1 TRDX = $${usdcPerTrdx.toFixed(2)} USDC`;
  } catch {
    return "Current rate shown at checkout.";
  }
}

/** @param {import("ethers").BigNumber | null | undefined} inventoryWei */
export function formatSaleInventoryLabel(inventoryWei) {
  if (inventoryWei == null) return "—";
  try {
    return `${formatTrdxAmount(Number(ethers.utils.formatUnits(inventoryWei, TRDX_DECIMALS)))} TRDX`;
  } catch {
    return "—";
  }
}

/**
 * @typedef {'config_missing' | 'read_failed' | 'paused' | 'no_inventory' | 'live'} TrdxSalePublicStatus
 */

/**
 * @returns {Promise<{
 *   envConfigured: boolean,
 *   publicStatus: TrdxSalePublicStatus,
 *   statusLabel: string,
 *   walletPurchaseEnabled: boolean,
 *   paused: boolean,
 *   hasInventory: boolean,
 *   inventoryWei: import("ethers").BigNumber | null,
 *   tokensPerEthWei: import("ethers").BigNumber | null,
 *   pricePerTrdxUsdcWei: import("ethers").BigNumber | null,
 * }>}
 */
export async function fetchTrdxSaleSnapshot() {
  const envConfigured = isTrdxSaleEnvExplicitlyConfigured();
  if (!envConfigured) {
    return {
      envConfigured: false,
      publicStatus: "config_missing",
      statusLabel: "Configuration pending",
      walletPurchaseEnabled: false,
      paused: false,
      hasInventory: false,
      inventoryWei: null,
      tokensPerEthWei: null,
      pricePerTrdxUsdcWei: null,
    };
  }

  try {
    const [paused, inventoryWei, tokensPerEthWei, pricePerTrdxUsdcWei] = await Promise.all([
      readPaused(),
      readSaleInventory(),
      readTokensPerEth(),
      readPricePerTrdxUsdc(),
    ]);
    const hasInventory = Boolean(inventoryWei && !inventoryWei.isZero());
    const isPaused = Boolean(paused);

    let publicStatus = "live";
    let statusLabel = "Live";
    if (isPaused) {
      publicStatus = "paused";
      statusLabel = "Sale paused";
    } else if (!hasInventory) {
      publicStatus = "no_inventory";
      statusLabel = "Wallet sale unavailable";
    }

    const walletPurchaseEnabled = envConfigured && !isPaused && hasInventory;

    return {
      envConfigured: true,
      publicStatus,
      statusLabel,
      walletPurchaseEnabled,
      paused: isPaused,
      hasInventory,
      inventoryWei,
      tokensPerEthWei,
      pricePerTrdxUsdcWei,
    };
  } catch {
    return {
      envConfigured: true,
      publicStatus: "read_failed",
      statusLabel: "Wallet sale unavailable",
      walletPurchaseEnabled: false,
      paused: false,
      hasInventory: false,
      inventoryWei: null,
      tokensPerEthWei: null,
      pricePerTrdxUsdcWei: null,
    };
  }
}
